// Card 56, the clan burgee (R548, ui-map-interface.md §17.3.56).
//
// A yacht club flies a burgee, a small triangular flag with the club's
// device, and a harbour chart notes the club at its moorings. Card 23 gave
// the faction its heraldry at the installation; this is the level below,
// and it shows the social layer of CONTEXT.md §5 on the map for the first
// time.
//
// **THE OWNER'S RULING (2026-09-05).** One burgee per SQUAD of the viewer's
// OWN clan, at the centroid of that squad's holds. Another clan shows
// nothing at all — a burgee over a stranger's hold would say more about the
// social map than the wire says about it. A viewer with no clan, and a
// squad that holds no water, both draw nothing.

import { chart as S } from '../../../gen/strings';
import { swallowTail } from '../../../render/chart-glyphs';
import { cellCenter, factionColor, factionName } from '../../../world';
import { SiteKind } from '../../../gen/proto/aquaphobia';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { faceOf, inkOf } from './shared';

/** One squad's burgee: where it flies and what it says. */
interface Burgee {
  x: number;
  y: number;
  squad: number;
}

export class ClanBurgeePiece implements Piece {
  readonly id = 'clan_burgee';
  readonly tier = 'screen';
  readonly pack = 'static';
  readonly film = 'hide';
  readonly rebuildOn = ['hello', 'clan', 'sites', 'reading', 'font'] as const;

  /** The burgees of this frame, by squad number. The tests read them. */
  burgees: Burgee[] = [];

  private tag = '';
  private clan = '';
  private faction = '';
  private ink = 0;

  /** No clan is no burgee, and so is a clan whose squads hold no water. */
  visible(ctx: ChartContext): boolean {
    return ctx.state.clan !== null;
  }

  rebuild(ctx: ChartContext): void {
    this.burgees = [];
    const clan = ctx.state.clan;
    if (!clan) return;
    this.tag = clan.tag;
    this.clan = clan.name;
    this.faction = factionName(ctx.state.faction);
    this.ink = factionColor(ctx.state.faction);

    // Which squad each clanmate sails in. Squad 0 is "not assigned", and
    // an unassigned member flies under no squad's burgee.
    const squadOf = new Map<number, number>();
    for (const m of clan.members) {
      if (m.squad > 0) squadOf.set(m.accountId, m.squad);
    }
    if (squadOf.size === 0) return;

    // The holds of each squad, in site order, so a centroid is the same
    // point whatever order the sites arrived in.
    const holds = new Map<number, { x: number; y: number; n: number }>();
    const sites = [...ctx.state.sites.values()].sort((a, b) => a.id - b.id);
    for (const s of sites) {
      if (s.kind !== SiteKind.SITE_KIND_PLAYER_BASE) continue;
      const squad = squadOf.get(s.ownerPlayer);
      if (squad === undefined) continue;
      const [x, y] = cellCenter(s.cell);
      const at = holds.get(squad) ?? { x: 0, y: 0, n: 0 };
      at.x += x;
      at.y += y;
      at.n += 1;
      holds.set(squad, at);
    }
    this.burgees = [...holds.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([squad, at]) => ({ x: at.x / at.n, y: at.y / at.n, squad }));
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    // The static pack culls nothing: it outlives every pan (R2238).
    const t = ctx.cfg.pieces.clan_burgee;
    const hist = ctx.reading === 'historical';
    const r = t[ctx.reading];
    const face = faceOf(ctx.reading);
    const ink = inkOf(r.ink, this.ink);
    for (const b of this.burgees) {
      const x = b.x + t.fig.dx, y = b.y + t.fig.dy;
      swallowTail(sinks.segments, x, y, r.fig.flag_w, r.fig.flag_h, ink, t.fig.alpha, r.width);
      const ly = y - r.fig.flag_h / 2;
      const lx = x + r.fig.flag_w + t.fig.label_gap;
      if (hist) {
        // The tag rides inside the fly, as the card set it.
        const f = t.historical.fig;
        ctx.glyphs.word(
          sinks.quads, this.tag, x + f.tag_dx, ly, face, t.text.tag, ink, t.fig.alpha,
        );
        ctx.glyphs.word(
          sinks.quads, S.burgee_historical({ clan: this.clan, faction: this.faction }),
          lx, ly, face, t.text.main, ink, r.alpha,
        );
      } else {
        ctx.glyphs.word(
          sinks.quads,
          S.burgee_modern({ tag: this.tag, clan: this.clan, squad: b.squad }),
          lx, ly, face, t.text.main, ink, r.alpha,
        );
      }
    }
  }
}

/** The registry's entry. */
export const clanBurgee = new ClanBurgeePiece();
